import styled, { css } from 'styled-components';
import {
  Checkbox as UICheckbox,
  Heading,
  IconButton,
  ToastType,
} from '@alefeducation/ui-elements-library-core';
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { TrashSolid } from '@alefeducation/ui-elements-library-icons';
import { AVATAR_HISTORY_ACTIONS, StyledStack } from './AvatarHistoryColHeader';
import { useAvatarUploadContext } from './context';
import { useAvatarInfoModal } from './AvatarInfoModal';
import { Checkbox } from '../Checkbox';
import {
  CATEGORY,
  DELETED,
  DISABLED,
  ENABLED,
  EXPIRY_DATE,
  FILE_NAME,
  GENDER,
  ORGANIZATION,
  STAR_COST,
  STATUS,
  TENANT,
} from '../../utils/constants';
import { AvatarHistory, AvatarStatus } from '../../pages/AvatarUpload/types';
import { UPDATE_AVATAR_STATUS } from '../../apollo/queries/avatarUpload/uploadHistory.queries';
import { injectAutoHideToastOfType } from '../../utils/Toast';
import Loading from '../Loading';
import { TagsView } from '../Common';
import { getAssetCategoryName } from '../../utils/assetCategories';

type Props = {
  item: AvatarHistory;
};

export const getStatus = (status: AvatarStatus): string => {
  return {
    [AvatarStatus.DELETED]: DELETED,
    [AvatarStatus.ENABLED]: ENABLED,
    [AvatarStatus.DISABLED]: DISABLED,
  }[status];
};

const AvatarRow = ({ item }: Props) => {
  const {
    id,
    fileName,
    tenants,
    organizations,
    category,
    genders,
    status,
    expiryDate,
    starCost,
    fileId,
    isEnabledForAllOrgs,
  } = item;
  const [avatarInfoModal, avatarInfoModalContext] = useAvatarInfoModal({
    name: fileName,
    fileId,
  });
  const [isEnabled, setIsEnabled] = useState(status === AvatarStatus.ENABLED);
  const [showActionElements, setShowActionElements] = useState(false);
  const tooltipId = `tooltip-${id}-${fileName}`;
  const { onAvatarDelete, selectedAvatarFileIds, toggleAvatar } =
    useAvatarUploadContext();
  const isSelected = selectedAvatarFileIds.includes(item.fileId);

  const [updateStatus, { loading }] = useMutation(UPDATE_AVATAR_STATUS, {
    onCompleted: (res) => {
      setIsEnabled((prev) => {
        injectAutoHideToastOfType(
          'avatar-upload-toast',
          `Successfully ${prev ? 'Disabled' : 'Enabled'}`,
          ToastType.SUCCESS,
          `You have successfully ${prev ? 'disabled' : 'enabled'} the avatar.`,
        );
        return !prev;
      });
    },
    onError: (res) => {
      injectAutoHideToastOfType(
        'avatar-upload-toast',
        `Something went wrong`,
        ToastType.ERROR,
        `We could not ${
          isEnabled ? 'disable' : 'enable'
        } the avatar right now.`,
      );
    },
  });

  function getStatusValue() {
    return getStatus(isEnabled ? AvatarStatus.ENABLED : AvatarStatus.DISABLED);
  }

  const onChange = () => {
    toggleAvatar('single', item.fileId);
  };

  function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }

  return (
    <StyledContainer
      selected={isSelected}
      data-testid="row-avatar"
      onMouseEnter={() => {
        setShowActionElements(true);
      }}
      onMouseLeave={() => {
        setShowActionElements(false);
      }}
    >
      {avatarInfoModalContext}
      <StyledStack name="select-all">
        <Checkbox
          value={item.fileId}
          checked={isSelected}
          onChange={onChange}
        />
      </StyledStack>
      <StyledStack
        justifyContent="flex-start"
        className="col-fname"
        data-testid="col-fname-1"
        key={`file-name-${id}`}
        name={FILE_NAME}
      >
        <StyledAvatarTitle onClick={avatarInfoModal.open}>
          <Heading variant="heading-small" color={'interactive.120'}>
            {fileName}
          </Heading>
        </StyledAvatarTitle>
      </StyledStack>

      <StyledStack
        key={`star-cost-${id}`}
        name={STAR_COST}
        data-testid="star-cost"
      >
        <Heading variant="heading-small" color={'primary.100'} weight="light">
          {String(starCost)}
        </Heading>
      </StyledStack>

      <StyledStack key={`tenant-${id}`} name={TENANT} display="flex">
        <TagsView items={tenants} tooltipId={`tooltip-countries-${id}`} />
      </StyledStack>

      <StyledStack
        key={`organization-${id}`}
        name={ORGANIZATION}
        columnGap={8}
        display="flex"
      >
        <TagsView
          items={
            isEnabledForAllOrgs
              ? ['For all and future orgs']
              : organizations.map((org) => org.name)
          }
          tooltipId={tooltipId}
        />
      </StyledStack>

      <StyledStack key={`category${id}`} name={CATEGORY}>
        <Heading variant="heading-small" color={'primary.100'} weight="light">
          {getAssetCategoryName(category) || ''}
        </Heading>
      </StyledStack>

      <StyledStack key={`gender-${id}`} name={GENDER}>
        <TagsView items={genders} tooltipId={`tooltip-genders-${id}`} />
      </StyledStack>

      <StyledStatus
        key={`status-${id}`}
        name={STATUS}
        className={'avatar-status'}
      >
        <div className={'status-toggle'}>
          {loading && <Loading className="loading" />}
          {showActionElements && !loading && (
            <span
              data-testid={'status-toggle'}
              onClick={() => {
                const status = isEnabled
                  ? AvatarStatus.DISABLED
                  : AvatarStatus.ENABLED;
                const variables = { avatarId: id, status };
                updateStatus({ variables });
              }}
            >
              <UICheckbox
                value=""
                variant="switch"
                checked={isEnabled}
                onChange={() => {}}
              >
                {' '}
              </UICheckbox>
            </span>
          )}
        </div>

        <Heading variant="heading-small" color={'primary.100'} weight="light">
          {getStatusValue()}
        </Heading>
      </StyledStatus>

      <StyledStack key={`expiry-date-${id}`} name={EXPIRY_DATE}>
        <Heading variant="heading-small" color={'primary.100'} weight="light">
          {expiryDate}
        </Heading>
      </StyledStack>
      <StyledStack key={`actions-${id}`} name={AVATAR_HISTORY_ACTIONS}>
        {showActionElements && selectedAvatarFileIds.length === 0 && (
          <IconButton
            onClick={() => {
              onAvatarDelete(item.fileId);
            }}
            size="small"
            tooltipContent="Delete"
            data-testid="icon-delete"
          >
            <TrashSolid />
          </IconButton>
        )}
      </StyledStack>
    </StyledContainer>
  );
};

export default AvatarRow;

const StyledContainer = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 24px;
  height: 45px;

  &:nth-child(odd) {
    background: #fbfbfc;
  }

  ${(props) =>
    props.selected
      ? css`
          background: ${props.theme.colors.interactive[10]} !important;
        `
      : ''}
`;

const StyledAvatarTitle = styled.span`
  &,
  * {
    text-decoration: underline;
  }

  text-decoration-color: ${(props) => props.theme.colors.interactive[10]};
  cursor: pointer;
`;

const StyledStatus = styled(StyledStack)`
  > div {
    display: flex;
    align-items: center;
    column-gap: 8px;

    label {
      padding: 0px;
    }

    .status-toggle {
      width: 30px;

      .loading svg {
        height: 18px;
      }
    }
  }
`;
